import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, ArrowRight, Zap, Network, Dna, Plane, Atom, Bot, Lock, Play,
  Compass, Hand, Activity, UserCheck, Wrench, BookOpen, Trophy,
} from "lucide-react";
import { Immersive as ImmersiveAPI, immersiveSession } from "../lib/immersive";

const SLUG_ICON = {
  "data-packet": Network,
  "human-cell": Dna,
  "aircraft": Plane,
  "power-plant": Atom,
  "break-ai": Bot,
};

const STAGE_PATTERN = [
  { k: "UNDERSTAND", Ico: BookOpen },
  { k: "EXPLORE", Ico: Compass },
  { k: "INTERACT", Ico: Hand },
  { k: "SIMULATE", Ico: Activity },
  { k: "EXPERIENCE", Ico: UserCheck },
  { k: "SOLVE", Ico: Wrench },
  { k: "LEARN", Ico: Trophy },
];

export default function Immersive() {
  const [list, setList] = useState([]);
  const session = immersiveSession();

  useEffect(() => {
    ImmersiveAPI.event("experience_opened", { session_id: session });
  }, [session]);

  useEffect(() => {
    ImmersiveAPI.experiences().then((d) => setList(d.experiences));
  }, []);

  return (
    <div className="mythos-ink-bg min-h-screen" data-testid="immersive-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-50 pointer-events-none" aria-hidden>
          <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, #7CE4FF33 0%, transparent 70%)" }} />
          <div className="absolute top-20 right-10 w-[420px] h-[420px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, #E07AFF22 0%, transparent 70%)" }} />
          <div className="absolute bottom-0 left-1/3 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, #C9F26B22 0%, transparent 70%)" }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-24 md:pt-32 pb-16">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase mythos-gold font-bold mb-6" data-testid="immersive-badge">
            <Sparkles size={14} /> EduSphere · Immersive Learning Lab
          </div>
          <h1 className="font-mythos text-5xl md:text-7xl lg:text-[88px] font-extrabold tracking-tight leading-[1.0] text-[#F4EFE6] max-w-5xl">
            Don't read about it. <span className="mythos-gold">Become it.</span>
          </h1>
          <p className="font-mythos text-lg md:text-2xl text-[#F4EFE6]/85 mt-6 max-w-3xl leading-relaxed">
            Five interactive worlds. One reusable engine. Every learner walks away understanding — not memorising — what makes the modern world work.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/immersive/future" className="mythos-gold-bg text-[#0E0F12] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:opacity-90 transition" data-testid="immersive-future-cta">
              SHOW ME THE FUTURE <Play size={14} />
            </Link>
            <a href="#experiences" className="border border-[#C9A66B] text-[#C9A66B] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:bg-[#C9A66B] hover:text-[#0E0F12] transition" data-testid="immersive-experiences-cta">
              EXPLORE 5 EXPERIENCES <ArrowRight size={16} />
            </a>
          </div>

          {/* 7-stage spine */}
          <div className="mt-16 border-t border-[#2A3349]/60 pt-10">
            <div className="text-[11px] tracking-[0.28em] uppercase mythos-gold font-bold mb-5">The ExperienceEngine spine</div>
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              {STAGE_PATTERN.map((s, i) => (
                <React.Fragment key={s.k}>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                    className="inline-flex items-center gap-2 rounded-full px-3 py-2 border"
                    style={{ background: "rgba(201,166,107,0.07)", borderColor: "rgba(201,166,107,0.3)" }}
                  >
                    <s.Ico size={14} className="mythos-gold" />
                    <span className="font-mythos text-xs tracking-[0.2em] font-bold text-[#F4EFE6]">{s.k}</span>
                  </motion.div>
                  {i < STAGE_PATTERN.length - 1 && <ArrowRight size={12} className="text-[#C9A66B]/50" />}
                </React.Fragment>
              ))}
            </div>
            <div className="text-xs text-[#F4EFE6]/55 mt-4 max-w-3xl">
              Same engine. Different stories. Each experience is a 7-stage journey — the learner starts with curiosity and ends with mastery.
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIENCES GRID */}
      <section id="experiences" className="max-w-7xl mx-auto px-5 md:px-10 pb-20">
        <div className="text-[11px] tracking-[0.28em] uppercase mythos-gold font-bold mb-3">Five experiences</div>
        <h2 className="font-mythos text-3xl md:text-5xl font-extrabold tracking-tight text-[#F4EFE6]">
          Pick a world.
        </h2>
        <p className="text-[#F4EFE6]/65 mt-3 max-w-2xl">The flagship is live. The other four ship with the next sprint — tap a card to preview the trailer.</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10" data-testid="immersive-grid">
          {list.map((e, i) => {
            const Ico = SLUG_ICON[e.slug] || Sparkles;
            const live = e.status === "available";
            return (
              <motion.div
                key={e.slug}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="group relative rounded-3xl overflow-hidden border transition"
                style={{ background: e.cover.accent_dark, borderColor: `${e.cover.accent}44` }}
                data-testid={`immersive-card-${e.slug}`}
              >
                {/* top ribbon */}
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
                  <div className="text-[9px] tracking-[0.24em] uppercase font-bold px-2 py-1 rounded-full" style={{ background: `${e.cover.accent}22`, color: e.cover.accent, border: `1px solid ${e.cover.accent}66` }}>
                    {live ? "LIVE DEMO" : "PREVIEW"}
                  </div>
                  <div className="text-[9px] tracking-[0.24em] uppercase font-bold" style={{ color: e.cover.accent }}>
                    {e.duration_min} min · {e.difficulty}
                  </div>
                </div>

                {/* animated cover */}
                <CoverArt accent={e.cover.accent} accentDark={e.cover.accent_dark} theme={e.cover.theme} label={e.cover.hero_label} />

                <div className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Ico size={20} style={{ color: e.cover.accent }} />
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: e.cover.accent }}>{e.subject}</div>
                  </div>
                  <div className="font-mythos font-extrabold text-2xl text-[#F4EFE6] leading-tight">{e.title}</div>
                  <div className="text-sm mt-1" style={{ color: e.cover.accent }}>{e.tagline}</div>
                  <p className="text-sm text-[#E8EBF0]/70 mt-4 leading-relaxed min-h-[72px]">{e.hook}</p>

                  <div className="flex flex-wrap gap-1.5 mt-5">
                    {e.capabilities.slice(0, 3).map((cap) => (
                      <span key={cap} className="text-[9px] tracking-[0.14em] uppercase font-bold px-2 py-1 rounded-full border" style={{ borderColor: `${e.cover.accent}44`, color: `${e.cover.accent}` }}>{cap}</span>
                    ))}
                  </div>

                  <Link
                    to={`/immersive/${e.slug}`}
                    className="mt-6 inline-flex items-center gap-2 font-bold tracking-wide text-sm hover:gap-3 transition-all"
                    style={{ color: e.cover.accent }}
                    data-testid={`immersive-enter-${e.slug}`}
                  >
                    {live ? "ENTER THE EXPERIENCE" : "PREVIEW THE TRAILER"} <ArrowRight size={14} />
                  </Link>
                </div>

                {!live && (
                  <div className="absolute top-16 right-4 opacity-70">
                    <Lock size={14} style={{ color: e.cover.accent }} />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* FOOTER CTA */}
        <div className="mt-20 rounded-3xl p-8 md:p-12 border" style={{ background: "radial-gradient(circle at 20% 20%, #1a1f2e 0%, #0A0D15 70%)", borderColor: "#C9A66B55" }}>
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="text-[11px] tracking-[0.28em] uppercase mythos-gold font-bold">For VCs, schools, museums</div>
              <h3 className="font-mythos text-3xl md:text-4xl font-extrabold tracking-tight text-[#F4EFE6] mt-3">One engine. A thousand experiences possible.</h3>
              <p className="text-[#F4EFE6]/70 mt-3 max-w-2xl">The ExperienceEngine is the real IP. Add new subjects by writing a stages[] file — no new code. Immersive history, chemistry, finance, surgery — all accessible to the same spine.</p>
            </div>
            <Link to="/xr/partners" className="mythos-gold-bg text-[#0E0F12] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:opacity-90 transition" data-testid="immersive-partner-cta">
              Partner with us <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------- Cover art animations per theme ----------
function CoverArt({ accent, accentDark, theme, label }) {
  return (
    <div className="relative h-44 overflow-hidden" style={{ background: `linear-gradient(135deg, ${accentDark} 0%, #050810 100%)` }}>
      <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" viewBox="0 0 200 100">
        <defs>
          <pattern id={`grid-${theme}`} width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke={`${accent}22`} strokeWidth="0.3" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#grid-${theme})`} />
        {theme === "cyber" && <CyberArt accent={accent} />}
        {theme === "bio" && <BioArt accent={accent} />}
        {theme === "sky" && <SkyArt accent={accent} />}
        {theme === "reactor" && <ReactorArt accent={accent} />}
        {theme === "neural" && <NeuralArt accent={accent} />}
      </svg>
      <div className="absolute bottom-3 left-4 text-[9px] tracking-[0.24em] uppercase font-mono font-bold" style={{ color: `${accent}99` }}>
        {label}
      </div>
    </div>
  );
}

function CyberArt({ accent }) {
  const nodes = [[20, 70], [50, 40], [80, 60], [120, 30], [160, 55], [180, 80]];
  return (
    <>
      {nodes.map((n, i) => (
        <React.Fragment key={i}>
          {nodes[i + 1] && <line x1={n[0]} y1={n[1]} x2={nodes[i + 1][0]} y2={nodes[i + 1][1]} stroke={accent} strokeWidth="0.6" opacity="0.5" />}
          <circle cx={n[0]} cy={n[1]} r="2" fill={accent}>
            <animate attributeName="r" values="2;4;2" dur={`${2 + (i * 0.3)}s`} repeatCount="indefinite" />
          </circle>
        </React.Fragment>
      ))}
      {nodes.slice(0, -1).map((n, i) => (
        <circle key={`pk-${i}`} r="1" fill={accent}>
          <animate attributeName="cx" from={n[0]} to={nodes[i + 1][0]} dur="2s" repeatCount="indefinite" begin={`${i * 0.4}s`} />
          <animate attributeName="cy" from={n[1]} to={nodes[i + 1][1]} dur="2s" repeatCount="indefinite" begin={`${i * 0.4}s`} />
        </circle>
      ))}
    </>
  );
}
function BioArt({ accent }) {
  return (
    <>
      {Array.from({ length: 7 }).map((_, i) => (
        <circle key={i} cx={30 + i * 25} cy={50 + Math.sin(i) * 15} r={5 + i} fill={accent} opacity={0.1 + i * 0.08}>
          <animate attributeName="r" values={`${4 + i};${7 + i};${4 + i}`} dur={`${3 + i * 0.3}s`} repeatCount="indefinite" />
        </circle>
      ))}
      <path d="M 10 50 Q 50 20 100 50 T 190 50" stroke={accent} strokeWidth="0.6" fill="none" opacity="0.7" />
      <path d="M 10 50 Q 50 80 100 50 T 190 50" stroke={accent} strokeWidth="0.6" fill="none" opacity="0.4" />
    </>
  );
}
function SkyArt({ accent }) {
  return (
    <>
      <path d="M 20 80 Q 100 20 180 80" stroke={accent} strokeWidth="0.8" fill="none" opacity="0.6" />
      <polygon points="90,45 110,45 115,50 95,50" fill={accent} opacity="0.95">
        <animateMotion dur="4s" repeatCount="indefinite" path="M 0 0 Q 20 -10 40 0 T 80 0" />
      </polygon>
      {[20, 60, 110, 160].map((x, i) => (
        <circle key={i} cx={x} cy={20 + i * 3} r="1.5" fill={accent} opacity="0.3" />
      ))}
    </>
  );
}
function ReactorArt({ accent }) {
  return (
    <>
      <circle cx="100" cy="50" r="26" fill="none" stroke={accent} strokeWidth="0.5" />
      <circle cx="100" cy="50" r="18" fill="none" stroke={accent} strokeWidth="0.5" strokeDasharray="2 1.5" />
      <circle cx="100" cy="50" r="8" fill={accent} opacity="0.4">
        <animate attributeName="opacity" values="0.3;0.9;0.3" dur="1.4s" repeatCount="indefinite" />
      </circle>
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i / 8) * Math.PI * 2;
        return <circle key={i} cx={100 + Math.cos(a) * 26} cy={50 + Math.sin(a) * 26} r="1.5" fill={accent} />;
      })}
    </>
  );
}
function NeuralArt({ accent }) {
  const layers = [[30, [30, 50, 70]], [100, [20, 40, 60, 80]], [170, [35, 55, 75]]];
  const points = [];
  layers.forEach(([x, ys]) => ys.forEach((y) => points.push([x, y])));
  const l1 = layers[0][1], l2 = layers[1][1], l3 = layers[2][1];
  return (
    <>
      {l1.map((y1) => l2.map((y2) => <line key={`${y1}-${y2}`} x1={30} y1={y1} x2={100} y2={y2} stroke={accent} strokeWidth="0.3" opacity="0.3" />))}
      {l2.map((y1) => l3.map((y2) => <line key={`b-${y1}-${y2}`} x1={100} y1={y1} x2={170} y2={y2} stroke={accent} strokeWidth="0.3" opacity="0.3" />))}
      {points.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="2.5" fill={accent}>
          <animate attributeName="opacity" values="0.4;1;0.4" dur={`${1.5 + (i % 3) * 0.3}s`} repeatCount="indefinite" />
        </circle>
      ))}
    </>
  );
}
